import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContextProvider";
import DomainDetails from "../../Profile/DomainDetails";
import { useMediaQuery } from "react-responsive";
import Lottie from "lottie-react";
import lottie from "../sad.json";
import {
  Grid,
  List,
  Calendar,
  Shield,
  Star,
  Tag,
  ExternalLink,
  Settings,
  TrendingUp,
  Globe,
} from "lucide-react";
import { MdOutlineError } from "react-icons/md";
import { get } from "../../../utils/api";
import { FaRupeeSign } from "react-icons/fa";

function Domains() {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainInfo, setDomainInfo] = useState([]);
  const { loginUser } = useContext(AuthContext);
  const [view, setView] = useState("card"); // 'card' or 'list'
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const isTablet = useMediaQuery({
    query: "(min-width: 769px) and (max-width: 1024px)",
  });

  // Fetch owned domains from backend
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        if (!loginUser?.username) return;

        const data = await get(`/domain/userproducts?owner=${loginUser.username}`);
        setDomainInfo(data || []);
      } catch (error) {
        setDomainInfo([]);
      }
    };

    fetchDomains();
  }, [loginUser]);

  const handleGrid = () => setView("card");
  const handleList = () => setView("list");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {selectedDomain ? (
          <DomainDetails
            domain={selectedDomain}
            onClose={() => setSelectedDomain(null)}
          />
        ) : (
          <>
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 lg:p-8 mb-4 md:mb-6 border border-blue-100">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="p-2 md:p-3 rounded-xl bg-gold-dark-gradient flex-shrink-0">
                      <Globe className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold gold-main bg-clip-text text-transparent truncate">
                        My Domains
                      </h1>
                      <p className="text-sm md:text-base text-gray-600 mt-1">
                        Manage your portfolio
                      </p>
                    </div>
                  </div>

                  {/* View Toggle Buttons */}
                  <div className="flex gap-2 self-end sm:self-auto">
                    <button
                      onClick={handleGrid}
                      className={`p-2 md:p-3 rounded-xl transition-all ${
                        view === "card"
                          ? "bg-gold-dark-gradient text-white shadow-lg"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      title="Card View"
                    >
                      <Grid className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    <button
                      onClick={handleList}
                      className={`p-2 md:p-3 rounded-xl transition-all ${
                        view === "list"
                          ? "bg-gold-dark-gradient text-white shadow-lg"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      title="List View"
                    >
                      <List className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex flex-wrap gap-3 md:gap-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-gold-gradient p-1.5 md:p-2 rounded-lg flex-shrink-0">
                      <Shield className="w-4 h-4 md:w-5 md:h-5 gold-main" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xl md:text-2xl font-bold text-gray-900">
                        {domainInfo.length}
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-500">
                        Total Domains
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-gold-gradient p-1.5 md:p-2 rounded-lg flex-shrink-0">
                      <TrendingUp className="w-4 h-4 md:w-5 md:h-5 gold-main" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xl md:text-2xl font-bold text-gray-900">
                        {domainInfo.filter((d) => d.status === "Active").length}
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-500">
                        Active
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Domains Display */}
            {domainInfo.length > 0 ? (
              view === "card" ? (
                // Card View
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {domainInfo.map((domain, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200 group flex flex-col h-full"
                    >
                      {/* Card Header */}
                      <div className="bg-gold-gradient p-3 md:p-4 relative rounded-t-2xl">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg md:text-2xl font-bold gold-main break-all capitalize">
                              {domain?.name || "Untitled Domain"}
                            </h3>
                            {domain.status && (
                              <span
                                className={`inline-block mt-2 text-[#101c4c] text-[10px] md:text-xs font-semibold px-2 md:px-3 py-1 rounded-full backdrop-blur-sm capitalize ${
                                  domain.status === "Active"
                                    ? "bg-green-100/80"
                                    : "bg-yellow-100/80"
                                }`}
                              >
                                {domain.status}
                              </span>
                            )}
                          </div>

                          {domain.premium && (
                            <span className="bg-[#b99653] text-white text-[8px] md:text-[10px] font-bold px-2 md:px-3 py-1 rounded-full flex items-center gap-1 shadow-md uppercase tracking-wide flex-shrink-0">
                              <Star className="w-2 h-2 md:w-3 md:h-3 text-white" />
                              Premium
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-4 md:p-6 flex flex-col flex-1">
                        <div className="space-y-2 md:space-y-3 mb-3 md:mb-4 flex-1">
                          {domain.category && (
                            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                              <Tag className="w-3 h-3 md:w-4 md:h-4 text-[#b99653] flex-shrink-0" />
                              <span className="font-medium truncate">
                                {domain.category}
                              </span>
                            </div>
                          )}

                          {domain.registrar && (
                            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                              <Shield className="w-3 h-3 md:w-4 md:h-4 text-[#b99653] flex-shrink-0" />
                              <span className="truncate">
                                {domain.registrar}
                              </span>
                            </div>
                          )}

                          {domain.registered_date && (
                            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                              <Calendar className="w-3 h-3 md:w-4 md:h-4 text-[#b99653] flex-shrink-0" />
                              <span className="truncate">
                                {new Date(
                                  domain.registered_date
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          )}

                          {domain.rating && (
                            <div className="flex items-center gap-2 text-xs md:text-sm">
                              <Star className="w-3 h-3 md:w-4 md:h-4 text-[#fcdb6a] fill-current flex-shrink-0" />
                              <span className="font-semibold text-[#101c4c]">
                                {domain.rating}
                              </span>
                              <span className="text-gray-500">/ 5.0</span>
                            </div>
                          )}
                        </div>

                        {/* Price & Manage Button */}
                        <div className="mt-auto pt-3 md:pt-4 border-t border-[#b99653]/20">
                          <div className="flex items-center justify-between mb-2 md:mb-3">
                            <div className="flex items-center gap-1">
                              <FaRupeeSign className="w-4 h-4 md:w-5 md:h-5 text-[#b99653]" />
                              <span className="text-xl md:text-2xl font-bold text-[#101c4c]">
                                {(domain?.price || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedDomain(domain)}
                            className="w-full white-btn !border-[#b99653] flex items-center justify-center gap-2 text-sm md:text-base !py-2"
                          >
                            <Settings className="w-3 h-3 md:w-4 md:h-4" />
                            Manage Domain
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // List View
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-blue-100">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-gold-gradient border-b-2 border-blue-200">
                        <tr>
                          <th className="px-3 md:px-6 py-3 md:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Domain Name
                          </th>
                          <th className="px-3 md:px-6 py-3 md:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-3 md:px-6 py-3 md:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider hidden sm:table-cell">
                            Registrar
                          </th>
                          <th className="px-3 md:px-6 py-3 md:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider hidden md:table-cell">
                            Category
                          </th>
                          <th className="px-3 md:px-6 py-3 md:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-3 md:px-6 py-3 md:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {domainInfo.map((domain, index) => (
                          <tr
                            key={index}
                            className="hover:bg-blue-50 transition-colors"
                          >
                            <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-xs md:text-sm text-gray-900 truncate max-w-[150px] md:max-w-none">
                                  {domain?.name || "Untitled Domain"}
                                </span>
                                {domain.premium && (
                                  <span className="bg-yellow-100 text-yellow-800 text-[8px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded-full flex items-center gap-1 flex-shrink-0">
                                    <Star className="w-2 h-2 md:w-3 md:h-3 fill-current" />
                                    <span className="hidden sm:inline">
                                      Premium
                                    </span>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                              <span
                                className={`px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold capitalize ${
                                  domain.status === "Active"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {domain?.status || "Active"}
                              </span>
                            </td>
                            <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden sm:table-cell">
                              <span className="text-xs md:text-sm text-gray-600">
                                {domain?.registrar || "N/A"}
                              </span>
                            </td>
                            <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden md:table-cell">
                              <span className="text-sm font-medium text-gray-700">
                                {domain?.category || "-"}
                              </span>
                            </td>
                            <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                              <span className="text-sm md:text-lg font-bold">
                                ₹{(domain?.price || 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                              <button
                                onClick={() => setSelectedDomain(domain)}
                                className="white-btn !border-[#b99653] !py-1.5 md:!py-2 !px-3 md:!px-4 hover:bg-[#b99653] hover:text-white transition-all duration-300 flex items-center gap-1 md:gap-2 text-xs md:text-sm"
                              >
                                <Settings className="w-3 h-3 md:w-4 md:h-4" />
                                <span className="hidden sm:inline">Manage</span>
                                <span className="sm:hidden">•••</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile scroll hint */}
                  <div className="sm:hidden px-4 py-2 bg-gray-50 border-t border-gray-200 text-center">
                    <p className="text-xs text-gray-500">
                      ← Scroll horizontally to see more →
                    </p>
                  </div>
                </div>
              )
            ) : (
              <ZeroDomains />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ZeroDomains() {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-blue-100">
      <Lottie
        animationData={lottie}
        loop
        autoplay
        style={{
          width: "300px",
          height: "300px",
          background: "transparent",
          margin: "auto",
        }}
      />
      <div className="mt-6">
        <p className="text-xl font-semibold text-gray-700 mb-2 flex items-center justify-center gap-2">
          <MdOutlineError className="text-red-500 text-2xl" />
          No Domains Found
        </p>
        <p className="text-gray-500 mb-6">
          It seems you don't own any domains yet. Start building your portfolio
          today!
        </p>
        <Link to="/Marketplace">
          <button className="bg-gold-gradient text-black px-8 py-3 rounded-xl hover:shadow-lg transition-all font-semibold flex items-center gap-2 mx-auto">
            <ExternalLink className="w-5 h-5" />
            Browse Marketplace
          </button>
        </Link>
      </div>
    </div>
  );
}

export default Domains;
